import { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { FaCheckCircle, FaExclamationTriangle, FaQuestionCircle, FaTrophy, FaUser } from "react-icons/fa";
import AvailabilityModal from "./AvailabilityModal";
import PracticeModal from "./PracticeModal";
import PracticeCalendar from "./PracticeCalendar";
import { apiFetch } from "./api";
import { ymd, formatEventDateShort as formatEventDate, formatTime12 as formatTime12Hour } from "./utils";

function getPracticeStartTime(practice) {
  return practice?.startTime || practice?.start_time || practice?.time || null;
}
function getPracticeEndTime(practice) {
  return practice?.endTime || practice?.end_time || null;
}
function formatRegattaDateRange(startDate, endDate) {
  if (!startDate) return "";
  const fmt = (d) => {
    const x = new Date(`${d}T00:00:00`);
    return x.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  if (!endDate || endDate === startDate) return fmt(startDate);
  const s = new Date(`${startDate}T00:00:00`);
  const e = new Date(`${endDate}T00:00:00`);
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${fmt(startDate)} – ${e.getDate()}`;
  }
  return `${fmt(startDate)} – ${fmt(endDate)}`;
}

export default function ParentView({ user, activeTab = "home", onNavigateToCalendar }) {
  const [athletes, setAthletes] = useState([]);
  const [selectedAthleteId, setSelectedAthleteId] = useState(null);
  const [loadingAthletes, setLoadingAthletes] = useState(true);

  const [upcomingDays, setUpcomingDays] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [upcomingError, setUpcomingError] = useState("");

  const [regattas, setRegattas] = useState([]);
  const [loadingRegattas, setLoadingRegattas] = useState(false);

  const [practiceModal, setPracticeModal] = useState(null);
  const [calendarPracticeDetails, setCalendarPracticeDetails] = useState(null);
  const [loadingCalendarDetails, setLoadingCalendarDetails] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [unansweredThisWeek, setUnansweredThisWeek] = useState(0);

  const plural = (n, s) => (n === 1 ? s : `${s}s`);

  // Load athletes linked to this parent
  const loadAthletes = useCallback(async () => {
    if (!user?.id) return;

    setLoadingAthletes(true);
    try {
      const athleteList = await apiFetch("/parent/athletes");
      setAthletes(athleteList || []);

      // Auto-select first athlete if only one
      if (athleteList && athleteList.length === 1) {
        setSelectedAthleteId(athleteList[0].id);
      } else if (athleteList && athleteList.length > 0 && !selectedAthleteId) {
        setSelectedAthleteId(athleteList[0].id);
      }
    } catch (err) {
      console.error("Error loading athletes:", err);
      setAthletes([]);
    } finally {
      setLoadingAthletes(false);
    }
  }, [user?.id, selectedAthleteId]);

  // Load upcoming week practices for selected athlete
  const loadUpcomingWeek = useCallback(async () => {
    if (!selectedAthleteId) {
      setUpcomingDays([]);
      setUnansweredThisWeek(0);
      return;
    }

    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const from = ymd(start);
    const to = ymd(end);

    setLoadingUpcoming(true);
    setUpcomingError("");
    try {
      const practices = await apiFetch(
        `/parent/athlete/${selectedAthleteId}/practices?from=${from}&to=${to}`
      );

      // Group practices by day
      const practicesByDay = {};
      (practices || []).forEach((p) => {
        const dateKey = p.date.slice(0, 10);
        if (!practicesByDay[dateKey]) {
          practicesByDay[dateKey] = [];
        }
        practicesByDay[dateKey].push({
          ...p,
          rsvpStatus: p.rsvpStatus || p.attendance_status || null,
          rsvpResponseData: p.responseData || null,
        });
      });

      const days = [];
      for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
        const iso = ymd(cursor);
        const dayPractices = practicesByDay[iso] || [];

        days.push({
          date: iso,
          practices: dayPractices,
          stateInfo: { state: "on", note: "" },
        });
      }

      // Count unanswered practices
      const missingCount = days.reduce((sum, day) => {
        const activePractices = day.practices.filter((p) => (p.state || "on") !== "off");
        const unanswered = activePractices.filter((p) => !p.rsvpStatus).length;
        return sum + unanswered;
      }, 0);

      setUpcomingDays(days);
      setUnansweredThisWeek(missingCount);
    } catch (err) {
      console.error("loadUpcomingWeek error", err);
      setUpcomingError(err?.message || "Failed to load upcoming practices");
      setUpcomingDays([]);
      setUnansweredThisWeek(0);
    } finally {
      setLoadingUpcoming(false);
    }
  }, [selectedAthleteId]);

  // Load upcoming regattas for selected athlete
  const loadRegattas = useCallback(async () => {
    if (!selectedAthleteId) {
      setRegattas([]);
      return;
    }

    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setMonth(end.getMonth() + 6);

    const from = ymd(start);
    const to = ymd(end);

    setLoadingRegattas(true);
    try {
      const practices = await apiFetch(
        `/parent/athlete/${selectedAthleteId}/practices?from=${from}&to=${to}`
      );
      // Deduplicate multi-day regattas (API expands them into one row per day)
      const seenIds = new Set();
      const upcoming = (practices || [])
        .filter(p => p.type === "regatta" && (p.state || "on") !== "off")
        .map(p => ({
          ...p,
          rsvpStatus: p.rsvpStatus || p.attendance_status || null,
        }))
        .sort((a, b) => a.date.localeCompare(b.date))
        .filter(p => {
          if (seenIds.has(p.id)) return false;
          seenIds.add(p.id);
          return true;
        });
      setRegattas(upcoming);
    } catch (err) {
      console.error("loadRegattas error", err);
      setRegattas([]);
    } finally {
      setLoadingRegattas(false);
    }
  }, [selectedAthleteId]);

  const sendRSVP = useCallback(
    async (newStatus, practiceId, responses = null) => {
      if (!selectedAthleteId) return;
      if (!practiceId) return;

      try {
        await apiFetch(`/attendance/mine?as=${selectedAthleteId}`, {
          method: "POST",
          body: JSON.stringify({
            status: newStatus,
            practice_id: practiceId,
            responses: responses || null,
          }),
        });
      } catch (err) {
        console.error("sendRSVP error", err);
        return;
      }

      await loadUpcomingWeek();
    },
    [selectedAthleteId, loadUpcomingWeek]
  );

  const handleRegattaRsvp = useCallback(
    (newStatus, regattaId) => {
      // Optimistic update
      setRegattas(prev =>
        prev.map(r => r.id === regattaId ? { ...r, rsvpStatus: newStatus } : r)
      );
      sendRSVP(newStatus, regattaId);
    },
    [sendRSVP]
  );

  const handleRSVPClick = useCallback(
    (newStatus, practiceId) => {
      flushSync(() => {
        setUpcomingDays((prev) =>
          prev.map((day) => ({
            ...day,
            practices: day.practices.map((p) =>
              p.id === practiceId ? { ...p, rsvpStatus: newStatus } : p
            ),
          }))
        );
      });
      sendRSVP(newStatus, practiceId);
    },
    [sendRSVP]
  );

  // Load practice details for calendar modal (includes RSVP status)
  const loadCalendarPracticeDetails = useCallback(async (practiceId) => {
    if (!practiceId || !selectedAthleteId) return;
    try {
      setLoadingCalendarDetails(true);
      // Fetch practice details with athlete's attendance/RSVP status
      const url = `/parent/athlete/${selectedAthleteId}/practices?from=${ymd(new Date())}&to=${ymd(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000))}`;
      const practices = await apiFetch(url);
      const practice = practices.find(p => p.id === practiceId);
      setCalendarPracticeDetails(practice || null);
    } catch (err) {
      console.error("Error loading calendar practice details:", err);
      setCalendarPracticeDetails(null);
    } finally {
      setLoadingCalendarDetails(false);
    }
  }, [selectedAthleteId]);


  const calendarFetchPractices = useCallback(
    (from, to) => apiFetch(`/parent/athlete/${selectedAthleteId}/practices?from=${from}&to=${to}`),
    [selectedAthleteId]
  );

  useEffect(() => {
    loadAthletes();
  }, [loadAthletes]);

  useEffect(() => {
    loadUpcomingWeek();
  }, [loadUpcomingWeek]);

  useEffect(() => {
    loadRegattas();
  }, [loadRegattas]);

  const renderRsvpIcon = (status) => {
    const lc = (status || "").toLowerCase();
    if (lc === "available") return <FaCheckCircle style={{ color: "#28a745" }} />;
    if (lc === "not available") {
      return (
        <span className="statusIcon absent custom-absent" style={{ width: 16, height: 16 }}>
          <span className="absent-icon-inner">×</span>
        </span>
      );
    }
    return <FaQuestionCircle style={{ color: "#6c757d" }} />;
  };

  const renderRsvpButtons = (practice) => {
    const current = (practice.rsvpStatus || "").toLowerCase();
    const practiceId = practice.id;
    const isAvailable = current === "available";
    const isNotAvailable = current === "not available";

    return (
      <div className="availability-practice-actions" style={{ marginTop: "0.5rem" }}>
        <button
          className={`availability-btn availability-btn-available ${isAvailable ? "active" : ""}`}
          style={isAvailable ? { background: "#16a34a", color: "white", borderColor: "#16a34a" } : undefined}
          onClick={() => handleRSVPClick("available", practiceId)}
          aria-pressed={isAvailable}
        >
          ✓ Available
        </button>
        <button
          className={`availability-btn availability-btn-unavailable ${isNotAvailable ? "active" : ""}`}
          style={isNotAvailable ? { background: "#dc2626", color: "white", borderColor: "#dc2626" } : undefined}
          onClick={() => handleRSVPClick("not available", practiceId)}
          aria-pressed={isNotAvailable}
        >
          ✗ Not Available
        </button>
      </div>
    );
  };

  if (loadingAthletes) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <p>Loading athletes...</p>
      </div>
    );
  }

  if (athletes.length === 0) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <FaExclamationTriangle size={48} color="#ffc107" />
        <h2>No Athletes Linked</h2>
        <p>Your account is not linked to any athletes yet.</p>
        <p>Please contact your coach to link your athlete(s) to your parent account.</p>
      </div>
    );
  }

  // Calendar tab view
  if (activeTab === "calendar") {
    return (
      <div className="stack">
        <div className="card" style={{ padding: 12 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
            <FaUser style={{ marginRight: "0.5rem" }} />
            Select Athlete:
          </label>
          <select
            value={selectedAthleteId || ""}
            onChange={(e) => setSelectedAthleteId(Number(e.target.value))}
            style={{
              padding: "0.5rem",
              fontSize: "1rem",
              width: "100%",
              maxWidth: "400px",
              borderRadius: "4px",
              border: "1px solid var(--card-border)",
              background: "var(--bg-elevated)",
              color: "var(--text)",
            }}
          >
            {athletes.map((athlete) => (
              <option key={athlete.id} value={athlete.id}>
                {athlete.name} {athlete.team ? `(${athlete.team})` : ""}
              </option>
            ))}
          </select>
        </div>

        {!selectedAthleteId ? (
          <div className="card" style={{ padding: "2rem", textAlign: "center" }}>
            <p>Please select an athlete to view their calendar.</p>
          </div>
        ) : (
          <>
            <PracticeCalendar
              squads={[]}
              coach={null}
              userId={selectedAthleteId}
              teamColors={{}}
              canEditPractices={false}
              onPracticeClick={(practice) => {
                setPracticeModal({ practiceId: practice.id, practice });
                loadCalendarPracticeDetails(practice.id);
              }}
              fetchPractices={calendarFetchPractices}
            />
            {practiceModal && (
              <PracticeModal
                isOpen={Boolean(practiceModal)}
                practiceId={practiceModal.practiceId}
                practice={practiceModal.practice}
                onClose={() => {
                  setPracticeModal(null);
                  setCalendarPracticeDetails(null);
                }}
                hideExtendedDetails={true}
                showRsvpButtons={true}
                rsvpStatus={calendarPracticeDetails?.rsvpStatus || practiceModal.practice?.rsvpStatus}
                rsvpResponseData={calendarPracticeDetails?.responseData || practiceModal.practice?.rsvpResponseData || null}
                loadingDetails={loadingCalendarDetails}
                practiceDetails={calendarPracticeDetails ? { practiceInfo: calendarPracticeDetails } : null}
                onRsvp={async (newStatus, responses) => {
                  // Update RSVP
                  await sendRSVP(newStatus, practiceModal.practiceId, responses);
                  // Reload practice details to get updated status
                  await loadCalendarPracticeDetails(practiceModal.practiceId);
                  await loadUpcomingWeek();
                }}
              />
            )}
          </>
        )}
      </div>
    );
  }

  // Home tab view
  return (
    <div style={{ padding: "1rem", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Athlete Selector */}
      <div style={{ marginBottom: "1.5rem", padding: "1rem", background: "var(--card)", border: "1px solid var(--card-border)", borderRadius: "8px" }}>
        <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
          <FaUser style={{ marginRight: "0.5rem" }} />
          Select Athlete:
        </label>
        <select
          value={selectedAthleteId || ""}
          onChange={(e) => setSelectedAthleteId(Number(e.target.value))}
          style={{
            padding: "0.5rem",
            fontSize: "1rem",
            width: "100%",
            maxWidth: "400px",
            borderRadius: "4px",
            border: "1px solid var(--card-border)",
            background: "var(--bg-elevated)",
            color: "var(--text)",
          }}
        >
          {athletes.map((athlete) => (
            <option key={athlete.id} value={athlete.id}>
              {athlete.name} {athlete.team ? `(${athlete.team})` : ""}
            </option>
          ))}
        </select>

      </div>

      {!selectedAthleteId ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>Please select an athlete to view their schedule.</p>
        </div>
      ) : (
        <>
          {/* Availability Banner */}
          {unansweredThisWeek > 0 && (
            <div
              style={{
                background: "var(--warn-bg)",
                border: "1px solid var(--warn-border)",
                borderRadius: "8px",
                padding: "1rem",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "var(--warn)",
              }}
            >
              <div>
                <FaExclamationTriangle style={{ color: "var(--warn)", marginRight: "0.5rem" }} />
                <strong>Action Needed:</strong> {unansweredThisWeek} unanswered{" "}
                {plural(unansweredThisWeek, "practice")} this week
              </div>
             
            </div>
          )}

          {/* Upcoming Week */}
          <div>
            <h2>Next 7 Days</h2>
            {loadingUpcoming && <p>Loading practices...</p>}
            {upcomingError && (
              <div style={{ color: "var(--danger)", padding: "1rem", background: "var(--danger-bg)", border: "1px solid var(--danger-border)", borderRadius: "4px" }}>
                {upcomingError}
              </div>
            )}
            {!loadingUpcoming && !upcomingError && (
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                {upcomingDays.map((day) => {
                  const dateObj = new Date(day.date + "T00:00:00");
                  const dayName = dateObj.toLocaleDateString("en-US", { weekday: "short" });
                  const monthDay = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });

                  return (
                    <div
                      key={day.date}
                      style={{
                        border: "1px solid var(--card-border)",
                        borderRadius: "8px",
                        padding: "1rem",
                        background: "var(--card)",
                      }}
                    >
                      <div style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
                        {dayName}, {monthDay}
                      </div>

                      {day.practices.length === 0 ? (
                        <div style={{ color: "var(--muted)", fontSize: "0.9rem" }}>No practices scheduled</div>
                      ) : (
                        day.practices.map((practice) => (
                          <div
                            key={`${practice.id}-${practice.date}`}
                            style={{
                              padding: "0.75rem",
                              marginBottom: "0.5rem",
                              background: "var(--bg-elevated)",
                              borderRadius: "4px",
                              borderLeft: `4px solid ${
                                practice.rsvpStatus?.toLowerCase() === "available"
                                  ? "#28a745"
                                  : practice.rsvpStatus?.toLowerCase() === "not available"
                                  ? "#dc3545"
                                  : "#6c757d"
                              }`,
                              cursor: "pointer",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
                              <div style={{ marginRight: "0.5rem" }}>{renderRsvpIcon(practice.rsvpStatus)}</div>
                              <div>
                                <div style={{ fontWeight: "500" }}>{practice.name || "Practice"}</div>
                                <div style={{ fontSize: "0.9rem", color: "var(--muted)" }}>
                                  📅 {formatEventDate(practice.date)}
                                </div>
                                <div style={{ fontSize: "0.9rem", color: "var(--muted)" }}>
                                  ⏰ {getPracticeStartTime(practice)
                                    ? formatTime12Hour(getPracticeStartTime(practice))
                                    : "All Day"}
                                  {getPracticeEndTime(practice) &&
                                  getPracticeStartTime(practice) !== getPracticeEndTime(practice)
                                    ? ` - ${formatTime12Hour(getPracticeEndTime(practice))}`
                                    : ""}
                                </div>
                                {practice.location && (
                                  <div style={{ fontSize: "0.85rem", color: "var(--muted)" }}>
                                    📍 {practice.location}
                                  </div>
                                )}
                              </div>
                            </div>
                            {renderRsvpButtons(practice)}
                            {practice.note && (
                              <div
                                style={{
                                  marginTop: "0.5rem",
                                  fontSize: "0.85rem",
                                  color: "var(--muted)",
                                  fontStyle: "italic",
                                }}
                              >
                                Note: {practice.note}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!loadingUpcoming && !upcomingError && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={onNavigateToCalendar}
                >
                  View All
                </button>
              </div>
            )}
          </div>
          {/* Regatta Schedule */}
          <div style={{ marginTop: "1.5rem" }}>
            <h2 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <FaTrophy style={{ color: "#d97706" }} />
              Regatta Schedule
            </h2>
            {loadingRegattas && <p>Loading regattas...</p>}
            {!loadingRegattas && regattas.length === 0 && (
              <p style={{ color: "var(--muted)" }}>No upcoming regattas</p>
            )}
            {!loadingRegattas && regattas.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {regattas.map(r => {
                  const isAvailable = r.rsvpStatus === "available" || r.rsvpStatus === "Available";
                  const isNotAvailable = r.rsvpStatus === "not available" || r.rsvpStatus === "Not Available";
                  return (
                    <div
                      key={r.id}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                        padding: "0.75rem 1rem",
                        background: "var(--card)",
                        border: "1px solid var(--card-border)",
                        borderRadius: "8px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--muted)", minWidth: 90, whiteSpace: "nowrap" }}>
                          {formatRegattaDateRange(r.date, r.endDate || r.end_date)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: "0.95rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {r.name || "Regatta"}
                          </div>
                          {r.location && (
                            <div style={{ fontSize: "0.8rem", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              📍 {r.location}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="availability-practice-actions regatta-rsvp-actions">
                        <button
                          className={`availability-btn availability-btn-available ${isAvailable ? "active" : ""}`}
                          style={isAvailable ? { background: "#16a34a", color: "white", borderColor: "#16a34a" } : undefined}
                          onClick={() => handleRegattaRsvp("available", r.id)}
                          aria-pressed={isAvailable}
                        >
                          ✓ Available
                        </button>
                        <button
                          className={`availability-btn availability-btn-unavailable ${isNotAvailable ? "active" : ""}`}
                          style={isNotAvailable ? { background: "#dc2626", color: "white", borderColor: "#dc2626" } : undefined}
                          onClick={() => handleRegattaRsvp("not available", r.id)}
                          aria-pressed={isNotAvailable}
                        >
                          ✗ Not Available
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Availability Modal */}
      {showAvailabilityModal && selectedAthleteId && (
        <AvailabilityModal
          userId={selectedAthleteId}
          userType="athlete"
          onClose={() => {
            setShowAvailabilityModal(false);
            loadUpcomingWeek();
          }}
        />
      )}
    </div>
  );
}
